import React, { FC } from 'react'
import { CustomerStoryBackgroundContainer } from './style'

//* utils
import { LocaleCodes } from '../../../../constants/localization.constants'
import { ComponentAnnouncement } from '../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../models/others/menuGroup'

//* components
import GlobalContainer from '../../../global/globalContainer'
import KeyStats from '../../../global/keyStats'
import MainCTA from '../../../global/mainCta'
import RelatedCustomerStories from '../../../global/relatedCustomerStories'
import SEOTags from '../../../global/seoTags'
import Layout from '../../../layouts/layout'
import CustomerStoryHero from '../customerStoryHero'
import { RenderPageBody } from './subcomponent'

type Props = {
  pageContext: any
  path: string
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData?: any
}

const CustomerStoryPage: FC<Props> = ({
  pageContext,
  path,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  if (!pageContext.resourceData) return null

  const caseStudyData = {
    pageName: pageContext.resourceData.pageName || '',
    customerStoryMicrocopy: pageContext.resourceData.customerStoryMicrocopy || null,
    h1Title: pageContext.resourceData.h1Title || '',
    body: pageContext.resourceData.body || '',
    metaInformation: pageContext.resourceData.metaInformation || [],
    slug: pageContext.resourceData.slug || '/',
    breadcrumb: pageContext.resourceData.breadcrumb || null,
    spotlightStats: pageContext.resourceData.spotlightStats || null,
    bottomCta: pageContext.resourceData.bottomCta || null,
    seoMetadata: pageContext.resourceData.seoMetadata || null,
    microcopy: pageContext.resourceData.microcopy?.values || [],
    mainImage: {
      url: pageContext.resourceData.mainImage?.image?.file?.url || '/',
      alt: pageContext.resourceData.mainImage?.altText || '',
    },
  }

  const defaultLocale = pageContext.pageLocale || LocaleCodes.ENGLISH.code
  let hero: any

  if (caseStudyData.h1Title) {
    let heroProps = {
      h1Title: caseStudyData.h1Title ? caseStudyData.h1Title : '',
      image: {
        imageSrc: caseStudyData.mainImage.url,
        alt: caseStudyData.mainImage.alt,
      },
      pageTitle: caseStudyData.pageName,
      microcopy: caseStudyData.customerStoryMicrocopy,
      slug: caseStudyData.slug,
      metaInformation: caseStudyData.metaInformation,
      breadcrumb: caseStudyData.breadcrumb,
    }

    hero = <CustomerStoryHero {...heroProps} />
  }

  const spotlight = caseStudyData.spotlightStats ? (
    <KeyStats isCustomerStory={true} data={caseStudyData.spotlightStats}></KeyStats>
  ) : null

  const ctaSection = caseStudyData.bottomCta ? (
    <MainCTA data={caseStudyData.bottomCta}></MainCTA>
  ) : null

  const pageInner = (
    <div data-cy="CustomerStory">
      <SEOTags
        seoMetadata={caseStudyData.seoMetadata}
        path={path}
        slug={caseStudyData.slug}
        locale={defaultLocale}
        siteSection="Customer Story"
        availableLocales={pageContext.availableLocales}
      />

      {hero}
      {spotlight}

      <CustomerStoryBackgroundContainer>
        <GlobalContainer>
          <RenderPageBody caseStudyData={caseStudyData} />
        </GlobalContainer>
      </CustomerStoryBackgroundContainer>

      {pageContext.relatedCustomerStories && (
        <RelatedCustomerStories data={pageContext.relatedCustomerStories} />
      )}
      {ctaSection}
    </div>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      pageType="customerStory"
      slug={caseStudyData.slug}
      availableLocales={pageContext.availableLocales}>
      {pageInner}
    </Layout.Template>
  )
}

export default CustomerStoryPage
